import { Await, LoaderFunctionArgs, useNavigate } from 'react-router-dom'
import { useLoaderData } from 'react-router'
import { usePageHeaders } from '@/src/hooks/use-page-headers/usePageHeaders'
import ScoreIndicator from '@/src/components/score/ScoreIndicator'
import { VpButton, VpSkeleton } from '@vtmn-play/react'
import { useTranslation } from 'react-i18next'
import { Suspense } from 'react'
import { DiagnosisScoreDto } from '@/infra/api/generated/model'
import { getDiagnoses } from '@/infra/api/generated/diagnoses/diagnoses'

const { getDiagnosisScore } = getDiagnoses()

export const Loader = ({ params }: LoaderFunctionArgs) => {
  const { diagnosisId } = params
  return { diagnosisScore: getDiagnosisScore(diagnosisId!) }
}

const ScoreIndex = () => {
  const { diagnosisScore } = useLoaderData()
  return (
    <main className="h-full flex flex-col p-5">
      <Suspense fallback={<ScoreSkeleton />}>
        <Await resolve={diagnosisScore}>
          {(resolved) => <ScoreContent diagnosisScore={resolved} />}
        </Await>
      </Suspense>
    </main>
  )
}

type ScoreContentProps = {
  diagnosisScore: DiagnosisScoreDto
}

const ScoreContent = ({ diagnosisScore }: ScoreContentProps) => {
  const { family, score, diagnosisId } = diagnosisScore
  const navigate = useNavigate()
  const { t } = useTranslation('common', { keyPrefix: 'pages.score' })
  usePageHeaders(t('title'), family.name)
  const subTextKey = score < 100 ? 'identify-actions' : 'family-compliant'
  const buttonLabel = score < 100 ? 'go-to-action-button' : 'back-to-home-button'
  const handleButtonClick = () => {
    if (score < 100) {
      void navigate(`/diagnoses/${diagnosisId}/actions`, {
        viewTransition: true,
        state: { source: 'diagnosis' },
      })
    } else {
      void navigate('/', { viewTransition: true })
    }
  }
  return (
    <>
      <div className="h-full flex flex-col gap-5 justify-center text-center text-white">
        <span className="text-xl font-semibold">{t('your-score')}</span>
        <ScoreIndicator score={score} size="large" />
        <span className="whitespace-pre-line">{t(subTextKey)}</span>
      </div>
      <VpButton className="w-full" variant="secondary" shape="squared" onClick={handleButtonClick}>
        {t(buttonLabel)}
      </VpButton>
    </>
  )
}

const ScoreSkeleton = () => {
  return (
    <>
      <div className="h-full flex flex-col gap-5 items-center justify-center text-center">
        <VpSkeleton className="w-1/2 h-7" shape="rounded" variantStyle="on-brand" />
        <div className={`flex justify-center items-center text-white gap-8`}>
          <VpSkeleton className="h-16 w-16" variantStyle="on-brand" />
          <VpSkeleton className="h-12 w-24" shape="rounded" variantStyle="on-brand" />
        </div>
        <VpSkeleton className="w-3/4 h-12" shape="rounded" variantStyle="on-brand" />
      </div>
      <VpSkeleton variantStyle="on-brand" shape="squared" className="w-full h-13" />
    </>
  )
}

export default ScoreIndex
